import {useContext, useState} from 'react';
import {Button, Input, Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {deleteCartItems, editCartItems, getCoupons, getProductGroups, getProducts} from "../../api/api";
import {toast} from "react-toastify";
import handleUnauthorized401MutateError from "../../utils/handleUnauthorized401MutateError";
import AuthContext from "../../context/AuthContext";
import EditProductAddOns from "./EditCart";
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";
import Scrollbars from "react-custom-scrollbars-2";
import Loader from "../../components/Loader";

function CartItem({cartItem}) {
    const {refreshToken} = useContext(AuthContext)
    const [quantity, setQuantity] = useState(cartItem.quantity);
    const [deleteCartItemModal, setDeleteCartItemModal] = useState(false);
    const [editCartItemsModalOpen, setEditCartItemsModalOpen] = useState(false);
    const [supportsAddOns, setSupportsAddOns] = useState(false);
    const [couponField, setCouponField] = useState(false);
    const [selectedCoupon, setSelectedCoupon] = useState(cartItem.coupon || '');
    const [couponSearchQuery, setCouponSearchQuery] = useState('');
    const [coupons, setCoupons] = useState([]);
    const page = 1;
    const records = 9;

    const { refetch } = useQuery({
        queryKey: ['coupons'],
        queryFn: () => getCoupons(page, '', '', '', '', '', '', '', '', '', '', '', '', '', '',records),
        retry: false,
        onSuccess: (data) => {
            const applicableCoupons = data.results.filter(coupon =>
                coupon.applicable_to === "CART-ITEM" || coupon.applicable_to === "BOTH"
            );
            setCoupons(applicableCoupons);
        },
        onError: (err) => {
            handleUnauthorized401QueryError(err, refreshToken, refetch);
            if (err.response.status !== 401) {
                toast.error(`An error occurred! ${err?.response?.data?.detail}`);
            }
        },
    });

    const handleCouponClick = () => {
        setCouponField(prev => !prev);
    };

    const handleSelectCoupon = (selectedCoupon) => {
        setSelectedCoupon(selectedCoupon);
        const formData = new FormData();
        formData.append('coupon', selectedCoupon.code);
        editCartItemsMutation.mutate({
            formData: formData,
            cartItemId: cartItem.id
        });
    };

    const handleRemoveCoupon = () => {
        const formData = new FormData();
        formData.append('coupon', '');

        editCartItemsMutation.mutate({
            formData: formData,
            cartItemId: cartItem.id
        }, {
            onSuccess: () => {
                if (!toast.isActive('couponRemovedToast')) {
                    toast.success('Coupon Removed Successfully', { toastId: 'couponRemovedToast' });
                }
            }
        });
    };

    const handleCouponSearchChange = (e) => {
        setCouponSearchQuery(e.target.value);
    };

        const searchedCoupons = coupons.filter(coupon =>
            coupon.code.toLowerCase().includes(couponSearchQuery.toLowerCase())
        );

    const isProductGroup = cartItem.type !== 'PRODUCT';
    const fetchAddOns = isProductGroup ? getProductGroups : getProducts;
    const productOrGroupId = isProductGroup
        ? cartItem.product_group_add_on_item?.product_group?.id
        : cartItem.product_add_on_item?.product?.id;

    // Fetch add-on support
    const {data} = useQuery(
        [isProductGroup ? 'productGroupDetails' : 'productDetails', productOrGroupId],
        () => fetchAddOns(1, productOrGroupId),
        {
            enabled: !!productOrGroupId,
            onSuccess: (data) => {
                if (data.results.length > 0) {
                    const item = data.results[0];
                    const hasAddOns = isProductGroup
                        ? item.add_ons_supported?.add_on_products?.length > 0
                        : item.add_ons_supported?.add_on_products?.length > 0;
                    setSupportsAddOns(hasAddOns);
                }

            },
            onError: (err, variables) => {
                handleUnauthorized401MutateError(err, variables, data)
                if(err.response.status !== 401){
                    toast.error(`An error occurred! ${err?.response?.data?.details}`)
                }
            }
        }

    );

    const editCartItemsToggleModal = () => setEditCartItemsModalOpen(!editCartItemsModalOpen);
    const deleteCartItemModalToggle = () => setDeleteCartItemModal(!deleteCartItemModal);
    const queryClient = useQueryClient()
    const [isCouponToastShown, setIsCouponToastShown] = useState(false);

    const editCartItemsMutation = useMutation({
        mutationFn: editCartItems,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries(['cart']);
            setQuantity(data.quantity);

            if (data.coupon && !isCouponToastShown) {
                if (!toast.isActive('couponAppliedToast')) {
                    toast.success(`Coupon Applied: ${data.coupon}`, { toastId: 'couponAppliedToast' });
                    setIsCouponToastShown(true);
                }
            } else if (variables.formData.has('quantity')) {
                if (!toast.isActive('quantityChangedToast')) {
                    toast.success('Quantity Changed!', { toastId: 'quantityChangedToast' });
                }
            }
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, editCartItemsMutation, variables);
            if (err.response.status !== 401) {

                const couponError = err?.response?.data?.coupon?.[0] || err?.response?.data?.coupon?.coupon;
                const errorMessage = err?.response?.data?.product_add_on_item?.[0] ||
                    err?.response?.data?.product_group_add_on_item?.[0] || couponError;

                if (couponError) {
                    if (!toast.isActive('couponErrorToast')) {
                        toast.error(`Coupon Error: ${couponError}`, { toastId: 'couponErrorToast' });
                    }
                } else if (errorMessage) {
                    if (!toast.isActive('errorToast')) {
                        toast.error(`An error occurred! ${errorMessage}`, { toastId: 'errorToast' });
                    }
                }
            }
        }
    });

    const deleteCartItemMutation = useMutation({
        mutationFn: deleteCartItems,
        onSuccess: (data, variables) =>{
            // queryClient.invalidateQueries(['cart', cartItem.customer.email])
            queryClient.invalidateQueries(['cart'])
            // setQuantity(data.quantity)
            if (!toast.isActive('itemRemovedToast')) {
                toast.success('Cart Item Removed!', { toastId: 'itemRemovedToast' });
            }
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, editCartItemsMutation, variables)
            if(err.response.status !== 401){
                toast.error(`An error occurred! ${err?.response?.data?.detail}`)
            }
        }
    })

    const handleDecrementQuantity = async (e, id) => {
        e.preventDefault();
        let decrementedQuantity = Number(quantity) - 1
        if (decrementedQuantity < 1){
            deleteCartItemModalToggle()
        }
        else {
            const formData = new FormData();
            formData.append("quantity", decrementedQuantity);

            editCartItemsMutation.mutate({
                formData: formData, cartItemId: id})
        }
    }


    const handleIncrementQuantity = async (e, id) => {
        e.preventDefault();
        let incrementedQuantity = Number(quantity) + 1
        const formData = new FormData();
        formData.append("quantity", incrementedQuantity);

        editCartItemsMutation.mutate({
            formData: formData, cartItemId: id})
    };

    const handleQuantityChange = async (e, id) => {
        setQuantity(e.target.value)
        let newQuantity = Number(e.target.value)
        const formData = new FormData();
        formData.append("quantity", newQuantity);

        editCartItemsMutation.mutate({
            formData: formData, cartItemId: id})
        console.log("id", id, quantity, e.target.value)
    };

    const handleDeleteCartItem = async (e, id) => {
        // setQuantity(e.target.value)
        deleteCartItemMutation.mutate({cartItemId: id})
        // console.log("id", id, quantity, e.target.value)
    };


    return (
        <>
            <tr>
                <td>
                    <div className="d-flex">
                        <div className="flex-1 mr-n4">
                            <div style={{display:"flex"}}>
                                <span
                                    style={{
                                        textDecoration:
                                            cartItem?.product_add_on_item
                                                ? (cartItem?.product_add_on_item?.product?.status === "ACTIVE"
                                                    ? "none"
                                                    : "line-through")
                                                : cartItem?.product_group_add_on_item
                                                    ? (cartItem?.product_group_add_on_item?.product_group?.status === "ACTIVE"
                                                        ? "none"
                                                        : "line-through")
                                                    : "none"
                                    }}
                                >
                                    {cartItem.name.length > 19 ? `${cartItem.name.substring(0, 19)}...` : cartItem.name}
                                    {/*{cartItem?.product_add_on_item?.product?.status === "ACTIVE" ? null : <i className="fas fa-ban text-danger"></i>}*/}

                                    {/* Ban icon for inactive products */}
                                    {cartItem?.product_add_on_item &&
                                        cartItem?.product_add_on_item?.product?.status !== "ACTIVE" && (
                                            <i className="fas fa-ban text-danger ml-1"></i>
                                        )}

                                    {cartItem?.product_group_add_on_item &&
                                        cartItem?.product_group_add_on_item?.product_group?.status !== "ACTIVE" && (
                                            <i className="fas fa-ban text-danger ml-1"></i>
                                        )}
                                </span>
                            {supportsAddOns && (
                                <i onClick={editCartItemsToggleModal} className="mdi mdi-pencil ml-1"></i>
                            )}
                            <Dropdown isOpen={couponField} toggle={handleCouponClick}>
                                <DropdownToggle
                                    className={`bx bxs-coupon ml-1 ${cartItem.coupon || couponField ? 'text-primary' : ''}`}
                                    tag="i"
                                    caret={false}
                                />
                                <DropdownMenu className="dropdown-coupon">
                                    <Input
                                        type="text"
                                        placeholder="Search coupons..."
                                        value={couponSearchQuery}
                                        onChange={handleCouponSearchChange}
                                        className="mb-2"
                                    />
                                    <Scrollbars style={{height: "20vh"}}>
                                    {searchedCoupons.length > 0 ? (
                                        searchedCoupons.map(coupon => (
                                            <DropdownItem className="dropdown-item" key={coupon.id} onClick={() => handleSelectCoupon(coupon)}>
                                                <strong>{coupon.code.substring(0, 20)}</strong><br />
                                            </DropdownItem>
                                        ))
                                    ) : (
                                        <DropdownItem disabled>
                                            No coupons found
                                        </DropdownItem>
                                    )}
                                    </Scrollbars>
                                </DropdownMenu>
                            </Dropdown>
                            </div>

                            {cartItem?.type==='PRODUCT' && cartItem?.product_add_on_item?.add_on_product?.length > 0 ? cartItem?.product_add_on_item?.add_on_product.map (addOnProduct => (
                                <p className="mb-0 font-13 text-muted"
                                   style={{ textDecoration: addOnProduct.status === "ACTIVE" ? "none" : "line-through" }}
                                >
                                    {addOnProduct.name.length > 15 ? `${addOnProduct.name.substring(0, 15)}...` : addOnProduct.name}
                                    {addOnProduct.status === "ACTIVE" ? null : <i className="fas fa-ban text-danger"></i>}
                                </p>
                            )) : null}

                            {cartItem?.type === 'PRODUCT-GROUP' && cartItem?.product_group_add_on_item?.add_on_product?.length > 0 ? (
                                cartItem.product_group_add_on_item.add_on_product.map(addOnProduct => (
                                    <p className="mb-0 font-13 text-muted" key={addOnProduct.id}
                                       style={{ textDecoration: addOnProduct.status === "ACTIVE" ? "none" : "line-through" }}
                                    >
                                        {addOnProduct.name.length > 15 ? `${addOnProduct.name.substring(0, 15)}...` : addOnProduct.name}
                                        {addOnProduct.status === "ACTIVE" ? null : <i className="fas fa-ban text-danger"></i>}
                                    </p>
                                ))
                            ) : null}
                        </div>
                    </div>
                    {selectedCoupon && cartItem.coupon && (
                        <p className="mb-0 font-13 text-muted">
                            <b>Coupon: </b> {cartItem.coupon.substring(0,10)}
                            <span onClick={handleRemoveCoupon} style={{ cursor: 'pointer' }}>
                                <i className="bx bx-x-circle ml-1 text-danger"></i>
                            </span>
                        </p>
                    )}
                </td>
                <td>
                    <div className="d-flex">
                        <div className="flex-1">
                            {cartItem.rate}
                            {cartItem?.product_add_on_item?.add_on_product?.length > 0 ? cartItem?.product_add_on_item?.add_on_product.map (addOnProduct => (
                                <p className="mb-0 font-13 text-muted">{addOnProduct.price}</p>
                            )) : null}

                            {cartItem?.type==='PRODUCT-GROUP' && cartItem?.product_group_add_on_item?.add_on_product?.length > 0 ? cartItem?.product_group_add_on_item?.add_on_product.map (addOnProduct => (
                                <p className="mb-0 font-13 text-muted">{addOnProduct.price}</p>
                            )) : null}
                        </div>
                    </div>
                </td>
                <td style={{width: 150}}>

                    <div className="d-flex flex-column align-items-center">
                        <div className="d-flex align-items-center">
                            <Button
                                color={"primary"}
                                className="btn-xs"
                                onClick={(e) => handleDecrementQuantity(e, cartItem.id)}
                            >
                                -
                            </Button>

                            <Input
                                type="text"
                                name="quantity"
                                id="cartItemQuantity"
                                style={{textAlign: "center"}}
                                value={quantity}
                                onChange={(e) => handleQuantityChange(e, cartItem.id)}
                            />
                            <Button color={"primary"} className="btn-xs" onClick={(e) => handleIncrementQuantity(e, cartItem.id)}>
                                +
                            </Button>
                        </div>
                        <Button
                            color="link"
                            className="text-danger"
                            style={{ padding: '0', marginTop: '5px' }}
                            onClick={deleteCartItemModalToggle}
                        >
                            <i className="mdi mdi-trash-can"></i>
                        </Button>
                    </div>
                </td>

                {/*<td>*/}
                {/*    <div className="d-flex">*/}
                {/*        <div className="flex-1">*/}
                {/*            {cartItem.amount_total_before_tax_before_discount}*/}
                {/*            <p className="mb-0 font-13 text-muted">tax: {cartItem.tax_amount}</p>*/}
                {/*        </div>*/}
                {/*    </div>*/}
                {/*    /!*{cartItem.amount_total_after_tax}*!/*/}
                {/*</td>*/}
                <td>
                    <div className="d-flex">
                        <div className="flex-1">

                            {Number(cartItem.coupon_discount) > 0 ? (
                                <>
                                    {/* Strike-through original amount */}
                                    <span style={{ textDecoration: "line-through", color: "#888", marginRight: "6px" }}>
                        {cartItem.amount_total_before_tax_before_discount}
                    </span>

                                    {/* Display discounted amount */}
                                    <span>
                        {cartItem.amount_total_before_tax_after_discount}
                    </span>
                                </>
                            ) : (
                                // No discount — show regular price
                                <span>{cartItem.amount_total_before_tax_before_discount}</span>
                            )}

                            <p className="mb-0 font-13 text-muted">
                                tax: {cartItem.tax_amount}
                            </p>
                        </div>
                    </div>
                </td>

            </tr>

            {editCartItemsModalOpen && (
                <EditProductAddOns
                    isOpen={editCartItemsModalOpen}
                    toggle={editCartItemsToggleModal}
                    productAddOnId={cartItem.type === 'PRODUCT' ? cartItem.product_add_on_item?.id : cartItem.product_group_add_on_item?.id}
                    productId={productOrGroupId}
                    existingAddOns={cartItem.type === 'PRODUCT' ? cartItem.product_add_on_item?.add_on_product : cartItem.product_group_add_on_item?.add_on_product}
                    isProductGroup={isProductGroup}
                    cartItem={cartItem}
                />
            )}

            <Modal isOpen={deleteCartItemModal} toggle={deleteCartItemModalToggle}>
                <ModalHeader toggle={deleteCartItemModalToggle} close={
                    <button className="close" onClick={deleteCartItemModalToggle}>
                        &times;
                    </button>
                }>Remove Cart Item</ModalHeader>
                <ModalBody>
                        <p>Are you sure you want to remove cart item {cartItem.name} from cart?</p>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color={"secondary"}
                        onClick={deleteCartItemModalToggle}
                    >
                        close
                    </Button>
                    <Button
                        color={"danger"}
                        disabled={deleteCartItemMutation.isLoading}
                        onClick={(e) => handleDeleteCartItem(e, cartItem.id)}
                    >
                        {deleteCartItemMutation.isLoading? <><Loader size={'small'}/>Removing cart item...</>: `Remove`}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default CartItem;